import { createURL } from 'expo-linking';

import { getEnvVars } from '@order/utils';

import { adjustLogger } from '../../adjust.logger';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Whether the URL is an Adjust branded link (e.g. `https://sweetgreen.go.link/rewards-test`).
 * The hosts are declared in `apps/order/app.config.ts` (`ADJUST_LINK_HOSTS`).
 * @see libs/order/integrations/adjust/src/machine/adjust-initialization-machine.ts
 */
export function checkIfAdjustLink(url: string): boolean {
  const host = getHost(url);

  return host !== null && getAdjustLinkHosts().includes(host);
}

/**
 * Maps a link resolved by the Adjust SDK to an app deep link the router can handle.
 */
export function getAppDeeplink(resolvedUrl: string): string | null {
  if (!checkIfAdjustLink(resolvedUrl)) return resolvedUrl;

  try {
    const url = new URL(resolvedUrl);
    const path = url.pathname.replace(/^\/+/, '');

    if (!path) return null;

    const adjustKeys = [...url.searchParams.keys()].filter((key) =>
      key.startsWith(ADJUST_PARAM_PREFIX),
    );

    if (adjustKeys.length === 0) {
      adjustLogger.warn('No Adjust keys found in the URL', { url });
      return null;
    }

    adjustKeys.forEach((key) => url.searchParams.delete(key));

    return createURL(path, { queryParams: Object.fromEntries(url.searchParams) });
  } catch {
    return null;
  }
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getHost(url: string): string | null {
  try {
    const { protocol, hostname } = new URL(url);

    if (protocol !== 'https:' && protocol !== 'http:') return null;

    return hostname.toLowerCase();
  } catch {
    return null;
  }
}

function getAdjustLinkHosts(): readonly string[] {
  const hosts: unknown = getEnvVars().ADJUST_LINK_HOSTS;

  return Array.isArray(hosts) ? hosts.map((host) => String(host).toLowerCase()) : [];
}

// ─── Constants ───────────────────────────────────────────────────────────────

const ADJUST_PARAM_PREFIX = 'adj_';
